import React, { useState, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import './frontend.css';
import { cloneBlock } from '@wordpress/blocks';

const HeroAnimations = ({ container }) => {
	const [isVisible, setIsVisible] = useState(false);
	const [animatedElements, setAnimatedElements] = useState(new Set());
	const [currentSlide, setCurrentSlide] = useState(1);
	const [isAutoSliding, setIsAutoSliding] = useState(true);
	const observerRef = useRef(null);
	const autoSlideRef = useRef(null);
	
	const animationDelay = parseFloat(container.dataset.animationDelay) || 0.5;
	const animationType = container.dataset.animationType || 'fade-up';
	const autoSlide = container.dataset.autoSlide === 'true';
	const slideInterval = parseFloat(container.dataset.slideInterval) || 5;

	// Slide switching function
	const switchSlide = (slideNumber) => {
		const slidesWrapper = container.querySelector('.hero-slides-wrapper');
		const pillToggle = container.querySelector('.pill-toggle');
		
		console.log('Switching to slide:', slideNumber);
		
		// Update pill toggle state
		if (pillToggle) {
			pillToggle.setAttribute('data-current-slide', slideNumber.toString());
		}
		
		// Update slides wrapper class to move the container
		if (slidesWrapper) {
			slidesWrapper.className = `hero-slides-wrapper slide-${slideNumber}`;
		}
		
		// Update current slide state
		setCurrentSlide(slideNumber);
		
		// Trigger animations for the new slide
		setTimeout(() => {
			triggerAnimations();
		}, 100);
	};

	// Auto slide functionality
	useEffect(() => {
		if (autoSlide && isAutoSliding) {
			autoSlideRef.current = setInterval(() => {
				setCurrentSlide(prev => {
					const nextSlide = prev === 1 ? 2 : 1;
					switchSlide(nextSlide);
					return nextSlide;
				});
			}, slideInterval * 1000);
		}

		return () => {
			if (autoSlideRef.current) {
				clearInterval(autoSlideRef.current);
			}
		};
	}, [autoSlide, isAutoSliding, slideInterval]);

	// Intersection Observer for triggering animations
	useEffect(() => {
		observerRef.current = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						setIsVisible(true);
						// Trigger animations with delays
						triggerAnimations();
					}
				});
			},
			{ threshold: 0.1 }
		);

		observerRef.current.observe(container);

		return () => {
			if (observerRef.current) {
				observerRef.current.disconnect();
			}
		};
	}, []);

	// Trigger animations with staggered delays
	const triggerAnimations = () => {
		const elements = container.querySelectorAll('[data-animation-delay]');
		
		elements.forEach((element, index) => {
			const delay = parseFloat(element.dataset.animationDelay) || 0;
			
			setTimeout(() => {
				element.classList.add('animate-in');
				setAnimatedElements(prev => new Set([...prev, element]));
			}, delay * 1000);
		});
	};

	// Add scroll indicator animation
	useEffect(() => {
		const scrollIndicator = container.querySelector('.hero-scroll-indicator');
		if (!scrollIndicator) return;

		const handleScroll = () => {
			const scrolled = window.pageYOffset;
			const opacity = Math.max(0, 1 - scrolled / 300);
			scrollIndicator.style.opacity = opacity;
		};

		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	// Parallax effect removed - background stays static

	// Add mouse movement parallax effect
	// useEffect(() => {
	// 	const heroContent = container.querySelector('.hero-content');
	// 	if (!heroContent) return;

	// 	const handleMouseMove = (e) => {
	// 		const { clientX, clientY } = e;
	// 		const { innerWidth, innerHeight } = window;
			
	// 		const xPos = (clientX / innerWidth - 0.5) * 20;
	// 		const yPos = (clientY / innerHeight - 0.5) * 20;
			
	// 		heroContent.style.transform = `translate(${xPos}px, ${yPos}px)`;
	// 	};

	// 	container.addEventListener('mousemove', handleMouseMove);
	// 	return () => container.removeEventListener('mousemove', handleMouseMove);
	// }, []);

	// // Add typing animation for title if specified
	// useEffect(() => {
	// 	const title = container.querySelector('.hero-title');
	// 	if (!title || !title.dataset.typing) return;

	// 	const text = title.textContent;
	// 	title.textContent = '';
		
	// 	let i = 0;
	// 	const typeWriter = () => {
	// 		if (i < text.length) {
	// 			title.textContent += text.charAt(i);
	// 			i++;
	// 			setTimeout(typeWriter, 100);
	// 		}
	// 	};

	// 	// Start typing after initial delay
	// 	setTimeout(typeWriter, animationDelay * 1000);
	// }, []);

	// Add floating animation to button
	useEffect(() => {
		const button = container.querySelector('.hero-button');
		if (!button) return;

		const floatAnimation = () => {
			button.style.transform = 'translateY(-5px)';
			setTimeout(() => {
				button.style.transform = 'translateY(0)';
			}, 2000);
		};

		// Start floating animation after button appears
		const buttonDelay = animationDelay + 0.6;
		setTimeout(() => {
			setInterval(floatAnimation, 4000);
		}, buttonDelay * 1000);
	}, []);

	// Video background handling
	useEffect(() => {
		const video = container.querySelector('.hero-video');
		if (!video) return;

		let loadingTimeout;
		let isVideoReady = false;

		// Set video attributes for faster loading
		video.preload = 'metadata';
		video.load(); // Force immediate loading

		// Handle video loading - show as soon as metadata is loaded
		const handleVideoMetadataLoaded = () => {
			console.log('Video metadata loaded');
			if (!isVideoReady) {
				isVideoReady = true;
				video.style.opacity = '1';
				container.classList.add('video-loaded');
				clearTimeout(loadingTimeout);
			}
		};

		// Handle video can play - backup trigger
		const handleVideoCanPlay = () => {
			console.log('Video can play');
			if (!isVideoReady) {
				isVideoReady = true;
				video.style.opacity = '1';
				container.classList.add('video-loaded');
				clearTimeout(loadingTimeout);
			}
		};

		// Handle video error - fallback to poster or gradient
		const handleVideoError = () => {
			console.warn('Video failed to load, falling back to poster image or gradient');
			video.style.display = 'none';
			container.classList.add('video-error');
			clearTimeout(loadingTimeout);
		};

		// Timeout fallback - show video after 2 seconds regardless
		const handleVideoTimeout = () => {
			console.log('Video loading timeout - showing anyway');
			if (!isVideoReady) {
				isVideoReady = true;
				video.style.opacity = '1';
				container.classList.add('video-loaded');
			}
		};

		// Add event listeners
		video.addEventListener('loadedmetadata', handleVideoMetadataLoaded);
		video.addEventListener('canplay', handleVideoCanPlay);
		video.addEventListener('error', handleVideoError);

		// Set initial opacity
		video.style.opacity = '0';
		video.style.transition = 'opacity 0.3s ease-in-out';

		// Set timeout fallback
		loadingTimeout = setTimeout(handleVideoTimeout, 2000);

		// Cleanup
		return () => {
			video.removeEventListener('loadedmetadata', handleVideoMetadataLoaded);
			video.removeEventListener('canplay', handleVideoCanPlay);
			video.removeEventListener('error', handleVideoError);
			clearTimeout(loadingTimeout);
		};
	}, []);

	// Pause video when not visible (performance optimization)
	useEffect(() => {
		const video = container.querySelector('.hero-video');
		if (!video) return;

		// Check if video has autoplay attribute
		const hasAutoplay = video.hasAttribute('autoplay');
		console.log('Video autoplay attribute:', hasAutoplay);

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						// Video is visible, play if autoplay is enabled
						console.log('Hero section visible - attempting to play video');
						if (hasAutoplay) {
							video.play().then(() => {
								console.log('Video resumed successfully');
							}).catch((error) => {
								console.warn('Video resume failed:', error);
							});
						}
					} else {
						// Video is not visible, pause to save resources
						console.log('Hero section not visible - pausing video');
						if (!video.paused) {
							video.pause();
							console.log('Video paused');
						}
					}
				});
			},
			{ threshold: 0.1 }
		);

		observer.observe(container);

		return () => {
			observer.disconnect();
		};
	}, []);

	// Add pill navigation event listeners
	useEffect(() => {
		// Pill toggle event listener
		const pillToggle = container.querySelector('.pill-toggle');
		
		const handlePillClick = (e) => {
			e.preventDefault();
			e.stopPropagation();
			const nextSlide = currentSlide === 1 ? 2 : 1;
			setIsAutoSliding(false); // Stop auto-sliding when user interacts
			switchSlide(nextSlide);
			
			// Jump to contact section when switching to slide 2
			if (nextSlide === 1) {
				console.log('Jumping to contact section');
				// Small delay to ensure slide transition completes
				setTimeout(() => {
					window.location.href = '/konfigurator';
				}, 300);
			}
		};
		
		if (pillToggle) {
			// Remove any existing listeners first
			pillToggle.removeEventListener('click', handlePillClick);
			pillToggle.addEventListener('click', handlePillClick);
			
			// Ensure the pill toggle has the correct initial state
			pillToggle.setAttribute('data-current-slide', currentSlide.toString());
		}
		
		// Add hover events to pause auto-sliding
		const handleMouseEnter = () => {
			setIsAutoSliding(false);
		};
		
		const handleMouseLeave = () => {
			if (autoSlide) {
				setIsAutoSliding(true);
			}
		};
		
		container.addEventListener('mouseenter', handleMouseEnter);
		container.addEventListener('mouseleave', handleMouseLeave);
		
		// Cleanup
		return () => {
			if (pillToggle) {
				pillToggle.removeEventListener('click', handlePillClick);
			}
			container.removeEventListener('mouseenter', handleMouseEnter);
			container.removeEventListener('mouseleave', handleMouseLeave);
		};
	}, [autoSlide, currentSlide]);

	// This component doesn't render anything - it just manages animations
	return null;
};

// Initialize React components for all hero containers
const initHeroBlocks = () => {
	const containers = document.querySelectorAll('.hero-container');
	
	containers.forEach(container => {
		// Skip if already initialized
		if (container.dataset.reactInitialized === 'true') return;
		
		// Create a wrapper div for React
		const reactWrapper = document.createElement('div');
		reactWrapper.style.display = 'contents'; // Don't affect layout
		container.appendChild(reactWrapper);
		
		// Create React root and render
		const root = createRoot(reactWrapper);
		root.render(<HeroAnimations container={container} />);
		
		// Mark as initialized
		container.dataset.reactInitialized = 'true';
	});
};

// Initialize when DOM is ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initHeroBlocks);
} else {
	initHeroBlocks();
}

// Re-initialize on dynamic content changes (for WordPress)
if (typeof MutationObserver !== 'undefined') {
	const observer = new MutationObserver((mutations) => {
		mutations.forEach((mutation) => {
			if (mutation.type === 'childList') {
				initHeroBlocks();
			}
		});
	});
	
	observer.observe(document.body, {
		childList: true,
		subtree: true
	});
}
